// ゲーム設定コンポーネント（ホスト専用）

import { useState } from "react";
import type { GameSettings, QuestionSet } from "../types";

type GameSettingsProps = {
  settings: GameSettings;
  questionSets: QuestionSet[];
  onUpdate: (settings: Partial<GameSettings>) => void;
  isHost: boolean;
};

export function GameSettingsPanel({
  settings,
  questionSets,
  onUpdate,
  isHost,
}: GameSettingsProps) {
  const [showSettings, setShowSettings] = useState(false);
  const [selectAllSets, setSelectAllSets] = useState(
    settings.selectedQuestionSetIds.length === 0
  );

  if (!isHost) {
    return null;
  }

  // 問題セット選択の処理
  const handleQuestionSetToggle = (setId: number) => {
    const currentIds = settings.selectedQuestionSetIds;
    const newIds = currentIds.includes(setId)
      ? currentIds.filter((id) => id !== setId)
      : [...currentIds, setId];
    onUpdate({ selectedQuestionSetIds: newIds });
  };

  const handleSelectAllToggle = () => {
    const newSelectAll = !selectAllSets;
    setSelectAllSets(newSelectAll);
    onUpdate({ selectedQuestionSetIds: newSelectAll ? [] : [] });
  };

  return (
    <div
      style={{
        marginBottom: "20px",
        padding: "15px",
        border: "2px solid #646cff",
        borderRadius: "8px",
        backgroundColor: "#1a1a2e",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h3 style={{ margin: 0, fontSize: "clamp(14px, 3vw, 16px)" }}>
          ⚙️ ゲーム設定（ホスト専用）
        </h3>
        <button
          onClick={() => setShowSettings(!showSettings)}
          style={{
            padding: "5px 15px",
            fontSize: "clamp(12px, 2.5vw, 14px)",
            cursor: "pointer",
          }}
        >
          {showSettings ? "閉じる" : "変更"}
        </button>
      </div>

      {showSettings && (
        <div style={{ marginTop: "15px" }}>
          {/* 勝利に必要な正答数 */}
          <div style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontSize: "clamp(12px, 2.5vw, 14px)",
              }}
            >
              勝利に必要な正答数:
            </label>
            <select
              value={settings.winScore}
              onChange={(e) => onUpdate({ winScore: parseInt(e.target.value) })}
              style={{
                padding: "8px",
                fontSize: "clamp(14px, 3vw, 16px)",
                borderRadius: "4px",
                border: "1px solid #646cff",
                backgroundColor: "#242424",
                color: "white",
                cursor: "pointer",
              }}
            >
              <option value={3}>3問正解</option>
              <option value={5}>5問正解</option>
              <option value={7}>7問正解</option>
            </select>
          </div>

          {/* 失格となる誤答数 */}
          <div style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontSize: "clamp(12px, 2.5vw, 14px)",
              }}
            >
              失格となる誤答数:
            </label>
            <select
              value={settings.maxWrongAnswers}
              onChange={(e) =>
                onUpdate({ maxWrongAnswers: parseInt(e.target.value) })
              }
              style={{
                padding: "8px",
                fontSize: "clamp(14px, 3vw, 16px)",
                borderRadius: "4px",
                border: "1px solid #646cff",
                backgroundColor: "#242424",
                color: "white",
                cursor: "pointer",
              }}
            >
              <option value={1}>1問不正解</option>
              <option value={3}>3問不正解</option>
              <option value={5}>5問不正解</option>
            </select>
          </div>

          {/* 問題数の上限 */}
          <div style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontSize: "clamp(12px, 2.5vw, 14px)",
              }}
            >
              問題数の上限:
            </label>
            <select
              value={settings.maxQuestions}
              onChange={(e) =>
                onUpdate({ maxQuestions: parseInt(e.target.value) })
              }
              style={{
                padding: "8px",
                fontSize: "clamp(14px, 3vw, 16px)",
                borderRadius: "4px",
                border: "1px solid #646cff",
                backgroundColor: "#242424",
                color: "white",
                cursor: "pointer",
              }}
            >
              <option value={10}>10問</option>
              <option value={30}>30問</option>
              <option value={50}>50問</option>
            </select>
          </div>

          {/* 問題文表示速度 */}
          <div style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontSize: "clamp(12px, 2.5vw, 14px)",
              }}
            >
              問題文表示速度:
            </label>
            <select
              value={settings.charInterval}
              onChange={(e) =>
                onUpdate({ charInterval: parseInt(e.target.value) })
              }
              style={{
                padding: "8px",
                fontSize: "clamp(14px, 3vw, 16px)",
                borderRadius: "4px",
                border: "1px solid #646cff",
                backgroundColor: "#242424",
                color: "white",
                cursor: "pointer",
              }}
            >
              <option value={50}>超高速（50ms）</option>
              <option value={75}>高速（75ms）</option>
              <option value={100}>標準（100ms）</option>
              <option value={150}>ゆっくり（150ms）</option>
              <option value={200}>超ゆっくり（200ms）</option>
            </select>
          </div>

          {/* 問題セット選択 */}
          <div style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontSize: "clamp(12px, 2.5vw, 14px)",
              }}
            >
              問題セット:
            </label>
            <div
              style={{
                padding: "10px",
                backgroundColor: "#2a2a3e",
                borderRadius: "4px",
              }}
            >
              {/* 全セット選択オプション */}
              <div style={{ marginBottom: "8px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    fontSize: "clamp(12px, 2.5vw, 14px)",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectAllSets}
                    onChange={handleSelectAllToggle}
                    style={{
                      marginRight: "8px",
                      cursor: "pointer",
                      width: "16px",
                      height: "16px",
                    }}
                  />
                  <span>全ての問題セットからランダム出題</span>
                </label>
              </div>

              {/* 個別の問題セット選択 */}
              {!selectAllSets && questionSets.length > 0 && (
                <div
                  style={{
                    marginTop: "10px",
                    paddingTop: "10px",
                    borderTop: "1px solid #444",
                  }}
                >
                  {questionSets.map((set) => (
                    <div key={set.id} style={{ marginBottom: "6px" }}>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          fontSize: "clamp(12px, 2.5vw, 14px)",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={settings.selectedQuestionSetIds.includes(
                            set.id
                          )}
                          onChange={() => handleQuestionSetToggle(set.id)}
                          style={{
                            marginRight: "8px",
                            cursor: "pointer",
                            width: "16px",
                            height: "16px",
                          }}
                        />
                        <span>{set.name}</span>
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {!selectAllSets && questionSets.length === 0 && (
                <div
                  style={{
                    marginTop: "10px",
                    fontSize: "clamp(10px, 2vw, 12px)",
                    color: "#888",
                  }}
                >
                  問題セットが読み込まれていません
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              marginTop: "15px",
              padding: "10px",
              backgroundColor: "#2a2a3e",
              borderRadius: "4px",
              fontSize: "clamp(10px, 2vw, 12px)",
              color: "#aaa",
            }}
          >
            設定はゲーム開始前にのみ変更できます
          </div>
        </div>
      )}
    </div>
  );
}
